import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { adminApi } from "../api/client";
import MetricCard from "../components/MetricCard";

interface DashboardPayload {
  statusSummary: Array<{ status: string; total: number }>;
  revenueSummary: {
    quotation_count: number;
    total_quoted_amount: number;
    granted_amount: number;
    pending_amount: number;
  };
  upcomingEvents: Array<Record<string, any>>;
  dueMilestones: Array<Record<string, any>>;
}

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0
});

const panelSx = {
  borderRadius: 3,
  border: "1px solid rgba(118, 93, 68, 0.12)",
  background: "linear-gradient(180deg, rgba(255, 252, 247, 0.98) 0%, rgba(255, 250, 244, 0.95) 100%)",
  boxShadow: "0 18px 42px rgba(105, 77, 49, 0.08)",
  overflow: "hidden"
};

interface DashboardTableColumn<T> {
  label: string;
  render: (row: T) => React.ReactNode;
}

function PaginatedDashboardTable<T extends Record<string, any>>({
  rows,
  columns,
  getRowKey
}: {
  rows: T[];
  columns: Array<DashboardTableColumn<T>>;
  getRowKey: (row: T, index: number) => string;
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.label} sx={{ fontWeight: 700 }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.map((row, index) => (
            <TableRow key={getRowKey(row, page * rowsPerPage + index)}>
              {columns.map((column) => (
                <TableCell key={column.label}>{column.render(row)}</TableCell>
              ))}
            </TableRow>
          ))}
          {!rows.length ? (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>
                  No data found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {rows.length ? (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      ) : null}
    </>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const dashboardData = await adminApi.get<DashboardPayload>("/reports/dashboard");
        setData(dashboardData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load dashboard");
      }
    };

    void load();
  }, []);

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          p: { xs: 2.75, md: 4.25 },
          borderRadius: 3,
          border: "1px solid rgba(191, 160, 130, 0.18)",
          background:
            "linear-gradient(135deg, rgba(63, 45, 33, 0.98) 0%, rgba(36, 25, 19, 0.98) 56%, rgba(87, 55, 34, 0.98) 100%)",
          color: "#fff8f2",
          boxShadow: "0 24px 58px rgba(63, 45, 33, 0.2)",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(115deg, rgba(255,255,255,0.08) 0%, transparent 34%), repeating-linear-gradient(135deg, rgba(240,201,130,0.11) 0 1px, transparent 1px 18px)",
            opacity: 0.7,
            pointerEvents: "none"
          },
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 5,
            background: "linear-gradient(90deg, #c08b57 0%, #d8b77d 34%, #8fb7a3 68%, #b56f6f 100%)"
          }
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: "rgba(239, 222, 206, 0.74)" }}>
              Operations command center
            </Typography>
            <Typography variant="h3" sx={{ mt: 1.25, fontWeight: 900, maxWidth: 760, fontSize: { xs: "2rem", md: "2.75rem" } }}>
              Run premium event quotations with clarity and control.
            </Typography>
            <Typography sx={{ mt: 1.5, maxWidth: 640, color: "rgba(239, 222, 206, 0.8)" }}>
              A focused operations workspace for pricing, approvals, customer coordination, scheduling, and reporting.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 2.5 }}>
              <Box sx={{ px: 1.5, py: 0.8, borderRadius: 2, border: "1px solid rgba(255,248,242,0.14)", background: "rgba(255,248,242,0.08)" }}>
                <Typography variant="caption" sx={{ color: "rgba(255,248,242,0.72)" }}>Total Pipeline</Typography>
                <Typography sx={{ fontWeight: 800 }}>{currencyFormatter.format(data?.revenueSummary.total_quoted_amount || 0)}</Typography>
              </Box>
              <Box sx={{ px: 1.5, py: 0.8, borderRadius: 2, border: "1px solid rgba(255,248,242,0.14)", background: "rgba(255,248,242,0.08)" }}>
                <Typography variant="caption" sx={{ color: "rgba(255,248,242,0.72)" }}>Active Quotes</Typography>
                <Typography sx={{ fontWeight: 800 }}>{data?.revenueSummary.quotation_count || 0}</Typography>
              </Box>
            </Stack>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button component={RouterLink} to="/admin/quotations/new" variant="contained" startIcon={<AddRoundedIcon />}>
              New quotation
            </Button>
            <Button component={RouterLink} to="/admin/quotations" variant="outlined" startIcon={<DescriptionRoundedIcon />} sx={{ color: "#fff8f2", borderColor: "rgba(255,248,242,0.22)" }}>
              Review pipeline
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
          gap: 2.5
        }}
      >
        <Box>
          <MetricCard
            eyebrow="Quotations"
            value={String(data?.revenueSummary.quotation_count || 0)}
            note="Total quotations in the system"
            accent="#8a5a3c"
            icon={<DescriptionRoundedIcon fontSize="small" />}
          />
        </Box>
        <Box>
          <MetricCard
            eyebrow="Quoted Amount"
            value={currencyFormatter.format(data?.revenueSummary.total_quoted_amount || 0)}
            note="Combined value of all quotations"
            accent="#2f7d67"
            icon={<TrendingUpRoundedIcon fontSize="small" />}
          />
        </Box>
        <Box>
          <MetricCard
            eyebrow="Granted Value"
            value={currencyFormatter.format(data?.revenueSummary.granted_amount || 0)}
            note="Approved quotation value"
            accent="#6f8f3d"
            icon={<TaskAltRoundedIcon fontSize="small" />}
          />
        </Box>
        <Box>
          <MetricCard
            eyebrow="Pending Value"
            value={currencyFormatter.format(data?.revenueSummary.pending_amount || 0)}
            note="Waiting for customer decision"
            accent="#b56f6f"
            icon={<PendingActionsRoundedIcon fontSize="small" />}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 2fr)" },
          gap: 2.5
        }}
      >
        <Box>
          <TableContainer component={Paper} elevation={0} sx={panelSx}>
            <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(118, 93, 68, 0.1)" }}>
              <InsightsRoundedIcon sx={{ color: "#8a5a3c" }} />
              <Typography sx={{ fontWeight: 800 }}>Quotation Status</Typography>
            </Stack>
            <PaginatedDashboardTable
              rows={data?.statusSummary || []}
              getRowKey={(row) => row.status}
              columns={[
                { label: "Status", render: (row) => row.status },
                { label: "Count", render: (row) => row.total }
              ]}
            />
          </TableContainer>
        </Box>
        <Box>
          <TableContainer component={Paper} elevation={0} sx={panelSx}>
            <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(118, 93, 68, 0.1)" }}>
              <EventAvailableRoundedIcon sx={{ color: "#2f7d67" }} />
              <Typography sx={{ fontWeight: 800 }}>Upcoming Events</Typography>
            </Stack>
            <PaginatedDashboardTable
              rows={data?.upcomingEvents || []}
              getRowKey={(row, index) => String(row.id || index)}
              columns={[
                { label: "Upcoming Event", render: (row) => String(row.title || "-") },
                { label: "Customer", render: (row) => String(row.customer_name || "-") },
                { label: "Date", render: (row) => row.event_start_at ? new Date(String(row.event_start_at)).toLocaleString("en-IN") : "-" }
              ]}
            />
          </TableContainer>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={panelSx}>
        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 2.5, py: 2, borderBottom: "1px solid rgba(118, 93, 68, 0.1)" }}>
          <PendingActionsRoundedIcon sx={{ color: "#b56f6f" }} />
          <Typography sx={{ fontWeight: 800 }}>Payment Milestones Due</Typography>
        </Stack>
        <PaginatedDashboardTable
          rows={data?.dueMilestones || []}
          getRowKey={(row, index) => String(row.id || index)}
          columns={[
            { label: "Quote", render: (row) => String(row.quote_number || "-") },
            { label: "Customer", render: (row) => String(row.customer_name || "-") },
            { label: "Milestone", render: (row) => String(row.milestone_label || "-") },
            { label: "Due Date", render: (row) => row.due_date ? new Date(String(row.due_date)).toLocaleDateString("en-IN") : "-" },
            { label: "Amount", render: (row) => currencyFormatter.format(Number(row.amount || 0)) }
          ]}
        />
      </TableContainer>
    </Stack>
  );
}
